import MetadataDragLabel from "../../elements/metadataDragLabel";
import {useIntegrator} from "../../integrator.context";
import Selectable from "../../elements/selectable.element";
import {useEffect, useState} from "react";
import {DropTargetMonitor, useDrop} from "react-dnd";
import {AddBuildingLevelCommand} from "../../commands/addBuildingLevel.command";
import MetadataLabel from "../../elements/metadataLabel.element";
import {RemoveBuildingRewardCommand} from "../../commands/removeBuildingReward.command";
import {RemoveBuildingLevelCommand} from "../../commands/removeBuildingLevel.command";
import {useApp} from "../../app.context";
import {AddBuildingRewardCommand} from "../../commands/addBuildingReward";
import {getDuplicateIds, getParameterSuffix} from "../../utils/metadata.utils";
import MetadataColumn from "../../elements/metadataColumn.element";
import {MetadataCollection} from "../../commands/metadataValue.command";
import {AddRewardMetadata} from "../../commands/addRewardMetadata.command";
import {RemoveRewardMetadata} from "../../commands/removeRewardMetadata";
import CityImprovementsPanel from "./cityImprovements.view";

function BuildingRewardsEditor() {
    const {state, executeCommand} = useIntegrator();
    const {getLocalizationValue, getAttributeManifestEntry} = useApp();
    const dupes = getDuplicateIds(state.rewards ?? []);

    // A building qualifies as a "city" when it has at least one starting slot;
    // CityUpgradePath is the only consumer of `rewards[level]`, so non-cities'
    // rewards are dead data and aren't editable here.
    const cities = state.buildings.filter(b => (b.properties?.startingSlots ?? 0) > 0);

    const [selectedId, setSelectedId] = useState<string>(cities[0]?.id ?? '');

    useEffect(() => {
        if (!cities.find(c => c.id === selectedId))
            setSelectedId(cities[0]?.id ?? '');
    }, [cities, selectedId]);

    const selectedCity = cities.find(c => c.id === selectedId);

    return (
        <div className={'layout horizontal'} style={{gap: 12, flexGrow: 1}}>
            <div className={'layout vertical gap'} style={{gap: 10, width: '21%', flexShrink: 0}}>
                {cities.length === 0 ? (
                    <div className={'box'} style={{padding: 20}}>
                        <p className={'muted-text'}>
                            No city building defined. In the Buildings tab, set <code>startingSlots</code> &gt; 0
                            on a building to mark it as a city, then return here to author its level-up rewards.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className={'layout vertical gap'} style={{flexGrow: 1, overflowY: 'auto', gap: 10, height: 0}}>
                            {/* Skip the dead Level 0 slot (see addBuilding.command.ts —
                                buildings start at runtime Level 1, so `rewards[0]` is
                                never used by CityUpgradePath). Visible labels mirror
                                the array index so the JSON stays grep-able. */}
                            {(selectedCity?.rewards ?? []).slice(1).map((rewards, indexFromOne) => {
                                const level = indexFromOne + 1;
                                return <LevelItem buildingId={selectedId} key={level} level={level} rewards={rewards}/>
                            })}
                        </div>
                        <button onClick={() => executeCommand(new AddBuildingLevelCommand(selectedId))}>Add Level</button>
                    </>
                )}
            </div>
            <div className={'layout vertical gap'} style={{gap: 10, width: '21.6%', flexShrink: 0}}>
                {/* Only show the city picker when there's more than one city —
                    in single-city branches (the common case) the picker is
                    noise. */}
                {cities.length > 1 && (
                    <Selectable style={{width: '100%'}} value={selectedId} options={cities.map(c => c.id)} onChange={v => setSelectedId(v)}/>
                )}
                <div className={'layout vertical gap box flex-grow'}>
                    <div className={'layout vertical gap'} style={{flexGrow: 1, overflowY: 'auto', height: 0, width: '100%'}}>
                        {(state.rewards ?? [])
                            .map(metadata => ({metadata, sortKey: buildRewardSortKey(metadata, getLocalizationValue)}))
                            .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
                            .map(({metadata}, index) => {
                                const suffix = getParameterSuffix(metadata, getAttributeManifestEntry(metadata.id, MetadataCollection.Rewards), getLocalizationValue);
                                return <MetadataDragLabel key={index} uid={metadata.uid} id={metadata.id} type={'attribute'}
                                                          values={metadata.values} duplicate={dupes.has(metadata.id)} draggable={() => true} suffix={suffix}/>
                            })}
                    </div>
                </div>
            </div>
            <MetadataColumn title={'Rewards'} attributes={state.rewards} collection={MetadataCollection.Rewards}
                            add={(uid) => executeCommand(new AddRewardMetadata(uid))}
                            remove={(uid) => executeCommand(new RemoveRewardMetadata(uid))}/>
            <div className={'layout vertical gap'} style={{gap: 10, width: '22.5%', flexShrink: 0}}>
                <CityImprovementsPanel/>
            </div>
        </div>
    )
}

function LevelItem(props: {buildingId: string, level: number, rewards: string[]}) {
    const {state, executeCommand} = useIntegrator();
    const {confirmDelete, getLocalizationValue, getAttributeManifestEntry} = useApp();

    const building = state.buildings.find(b => b.id === props.buildingId)

    function checkCanDrop(monitor: DropTargetMonitor<any, void>): boolean {
        if (building === undefined || !monitor.isOver({shallow: true})) {
            return false;
        }

        return !building.rewards[props.level]?.includes(monitor.getItem().id);
    }

    const [{canDrop}, drop] = useDrop({
        accept: 'attribute',
        drop: (item: any, monitor) => {
            if (!checkCanDrop(monitor)) {
                return;
            }

            executeCommand(new AddBuildingRewardCommand(props.buildingId, item.id, props.level));
        },
        collect: (monitor) => {
            return {
                canDrop: checkCanDrop(monitor)
            };
        },
    });

    return (
        <div ref={drop} className={'box'} style={{
            border: `solid 1px ${canDrop ? 'var(--tint-color)' : 'var(--overlay-color)'}`
        }}>
            <div className={'layout vertical gap'}>
                <div className={'layout horizontal stretch center gap'}>
                    <p className={'text header'}>{`Level ${props.level}`}</p>
                    <button onClick={async () => {
                        if (await confirmDelete(`Level ${props.level}`))
                            executeCommand(new RemoveBuildingLevelCommand(props.buildingId, props.level));
                    }}>
                        <span className={'fa-solid fa-trash'}/>
                    </button>
                </div>
                {props.rewards.map((reward, index) => {
                    const meta = state.rewards?.find(r => r.uid === reward);
                    const suffix = getParameterSuffix(meta, meta ? getAttributeManifestEntry(meta.id, MetadataCollection.Rewards) : undefined, getLocalizationValue);
                    return <MetadataLabel key={index} uid={reward} suffix={suffix} onDelete={() =>
                        executeCommand(new RemoveBuildingRewardCommand(props.buildingId, reward, props.level))
                    }/>
                })}
            </div>
        </div>
    )
}

// Sort key for the draggable rewards column: the reward's display name
// concatenated with its authored parameter values and entity-reference names,
// no separators (e.g. "Gold3", "GoldPerTurn6"). Lower-cased so the sort is
// case-insensitive and keeps "Gold3" / "gold3" adjacent.
function buildRewardSortKey(
    metadata: {uid: string, values?: {[k: string]: number}, entities?: {[k: string]: string}},
    getLocalizationValue: (key: string) => string
): string {
    const name = getLocalizationValue(metadata.uid);
    const values = Object.values(metadata.values ?? {}).map(v => String(v)).join('');
    const entities = Object.values(metadata.entities ?? {}).filter(Boolean).map(e => getLocalizationValue(e)).join('');
    return (name + values + entities).toLowerCase();
}

export default BuildingRewardsEditor;
